import { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  IconButton,
  Modal,
  CircularProgress,
  Button,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import { API_URL } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import formatPrice from "../utils/formatPrice";

interface ProductData {
  id: number;
  title: string;
  subtitle?: string;
  slug: string;
  category_name: string | null;
  prices: Array<{ id: number; label: string; amount: number }>;
  specs: Array<{ id: number; text: string }>;
  options: Array<{ id: number; name: string; amount: number }>;
  images: Array<{ id: number; url: string; sort_order: number }>;
  hero_image_id: number | null;
}

function ImageCarousel({ images }: { images: string[] }) {
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!images || images.length === 0) return null;

  const prev = () => setActive((i) => (i === 0 ? images.length - 1 : i - 1));
  const next = () => setActive((i) => (i === images.length - 1 ? 0 : i + 1));

  const lightboxPrev = (e: React.MouseEvent) => { e.stopPropagation(); prev(); };
  const lightboxNext = (e: React.MouseEvent) => { e.stopPropagation(); next(); };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          borderRadius: 1,
          overflow: "hidden",
          backgroundColor: "#1a1a1a",
          cursor: "zoom-in",
        }}
        onClick={() => setLightboxOpen(true)}
      >
        <Box
          component="img"
          src={images[active]}
          sx={{ width: "100%", height: 300, objectFit: "contain", display: "block", pointerEvents: "none" }}
          alt={`Image ${active + 1} of ${images.length}`}
        />
        {images.length > 1 && (
          <>
            <IconButton
              onClick={prev}
              size="small"
              sx={{
                position: "absolute",
                top: "50%",
                left: 8,
                transform: "translateY(-50%)",
                backgroundColor: "rgba(0,0,0,0.5)",
                color: "#fff",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
              }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={next}
              size="small"
              sx={{
                position: "absolute",
                top: "50%",
                right: 8,
                transform: "translateY(-50%)",
                backgroundColor: "rgba(0,0,0,0.5)",
                color: "#fff",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
              }}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>
      {images.length > 1 && (
        <Box sx={{ display: "flex", gap: 1, mt: 1, justifyContent: "center" }}>
          {images.map((img, i) => (
            <Box
              key={i}
              component="img"
              src={img}
              alt={`Thumbnail ${i + 1}`}
              onClick={() => setActive(i)}
              sx={{
                width: 60,
                height: 40,
                objectFit: "cover",
                borderRadius: 1,
                cursor: "pointer",
                border: i === active ? "2px solid #1976d2" : "2px solid transparent",
                opacity: i === active ? 1 : 0.6,
                "&:hover": { opacity: 1 },
              }}
            />
          ))}
        </Box>
      )}
      <Modal open={lightboxOpen} onClose={() => setLightboxOpen(false)} sx={{ zIndex: 1300 }}>
        <Box
          onClick={() => setLightboxOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.95)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{ position: "absolute", top: 16, right: 16, color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>
          {images.length > 1 && (
            <IconButton
              onClick={lightboxPrev}
              sx={{
                position: "absolute",
                left: 16,
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.1)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
          )}
          <Box
            component="img"
            src={images[active]}
            alt={`Image ${active + 1} of ${images.length}`}
            sx={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
              pointerEvents: "none",
            }}
          />
          {images.length > 1 && (
            <IconButton
              onClick={lightboxNext}
              sx={{
                position: "absolute",
                right: 16,
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.1)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          )}
        </Box>
      </Modal>
    </Box>
  );
}


function ProductCard({ product }: { product: ProductData }) {
  const { t } = useLang();
  const sortedImages = [...product.images].sort((a, b) => {
    if (a.id === product.hero_image_id) return -1;
    if (b.id === product.hero_image_id) return 1;
    return (a.sort_order ?? 0) - (b.sort_order ?? 0);
  });
  const imageUrls = sortedImages.map((img) =>
    img.url.startsWith("http") ? img.url : `${API_URL}${img.url}`
  );

  return (
    <Paper
      sx={{
        p: 4,
        backgroundColor: "#2a2a2a",
        color: "#f0f0f0",
        border: "1px solid #444",
        height: "100%",
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ color: "#1976d2", fontWeight: 700 }}>
        {product.title}
      </Typography>
      {product.subtitle && (
        <Typography variant="subtitle1" sx={{ mb: 2, opacity: 0.8 }}>
          {product.subtitle}
        </Typography>
      )}

      <ImageCarousel images={imageUrls} />

      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableBody>
            {product.prices.map((row) => (
              <TableRow key={row.id}>
                <TableCell sx={{ color: "#f0f0f0", borderColor: "#444", pl: 0 }}>
                  {row.label}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: "#4caf50", borderColor: "#444", fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {formatPrice(row.amount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {product.specs.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            {t("offer.standardEquipment")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {product.specs.map((s) => (
              <Chip key={s.id} label={s.text} variant="outlined" sx={{ color: "#ccc", borderColor: "#555" }} />
            ))}
          </Box>
        </>
      )}

      {product.options.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>
            {t("offer.optionalEquipment")}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {product.options.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell sx={{ color: "#f0f0f0", borderColor: "#444", pl: 0 }}>
                      {o.name}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "#4caf50", borderColor: "#444", fontWeight: 700, whiteSpace: "nowrap" }}
                    >
                      {formatPrice(o.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
}

export default function Oferta() {
  const { t, lang } = useLang();
  const [categories, setCategories] = useState<Array<{ id: number; name: string }>>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<number | string | null>(null);
  const [products, setProducts] = useState<ProductData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API_URL}/api/categories?lang=${lang}`)
      .then((res) => res.json())
      .then((data) => {
        setCategories(data);
        if (data.length > 0 && activeCategoryId === null) {
          setActiveCategoryId(data[0].id);
        } else if (data.length === 0) {
          setActiveCategoryId("__none__");
        }
      })
      .catch(() => {
        setActiveCategoryId("__none__");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (activeCategoryId === null) return;
    setLoading(true);
    setError(null);
    const url =
      activeCategoryId === "__none__"
        ? `${API_URL}/api/products?lang=${lang}`
        : `${API_URL}/api/products?lang=${lang}&category_id=${activeCategoryId}`;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(t("offer.error"));
        return res.json();
      })
      .then(setProducts)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [lang, activeCategoryId, t]);

  const handleCategoryChange = (catId: number) => {
    if (catId === activeCategoryId) {
      setLoading(true);
      setProducts([]);
      const url = `${API_URL}/api/products?lang=${lang}&category_id=${catId}`;
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(t("offer.error"));
          return res.json();
        })
        .then(setProducts)
        .catch((e: Error) => setError(e.message))
        .finally(() => setLoading(false));
      return;
    }
    setActiveCategoryId(catId);
  };

  return (
    <Box sx={{ backgroundColor: "#1a1a1a", minHeight: "100vh" }}>
      <Box sx={{ height: 64 }} />

      <Box
        sx={{
          py: 10,
          background: "linear-gradient(135deg, #1c1c1c 0%, #222 100%)",
          color: "#f0f0f0",
          textAlign: "center",
        }}
      >
        <Container>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>
            {t("offer.title")}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.8, mt: 1 }}>
            {t("offer.subtitle")}
          </Typography>
        </Container>
      </Box>

      <Container sx={{ py: 8 }}>
        {categories.length > 0 && (
          <Box sx={{ display: "flex", gap: 2, mb: 6, justifyContent: "center", flexWrap: "wrap" }}>
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={activeCategoryId === cat.id ? "contained" : "outlined"}
                onClick={() => handleCategoryChange(cat.id)}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: "1rem",
                  borderRadius: 2,
                  ...(activeCategoryId === cat.id
                    ? { backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#125ea8" } }
                    : { borderColor: "#555", color: "#ccc", "&:hover": { borderColor: "#1976d2", color: "#fff" } }),
                }}
              >
                {cat.name}
              </Button>
            ))}
          </Box>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: "#f0f0f0" }} />
          </Box>
        )}
        {error && (
          <Typography sx={{ color: "#f44336", textAlign: "center" }}>
            {error}
          </Typography>
        )}
        {!loading && !error && (
          <Grid container spacing={6}>
            {products.length === 0 && (
              <Grid item xs={12}>
                <Typography sx={{ color: "#888", textAlign: "center", py: 8 }}>
                  {t("offer.noProducts")}
                </Typography>
              </Grid>
            )}
            {products.map((p) => (
              <Grid item xs={12} md={6} key={p.id}>
                <ProductCard product={p} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}
